import { useT } from '@kovix/shell-kit/i18n';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useSession } from '@/app/auth/session.store';
import { ShellIcon } from './NavIcon';

/**
 * Nút "Trợ giúp" trên topbar (mockup v150 — X0‑e 18/09/2026): popover gồm hướng dẫn theo màn hiện tại (tài liệu ngoài theo `VITE_KOVIX_DOCS_URL/<navKey>`,
 * chưa cấu hình → báo rõ, không giả link), phím tắt, bản đồ nghiệp vụ & vai trò, trung tâm thông báo. Không gọi API.
 */
export function HelpMenu({ navKey }: { navKey: string | null }) {
  const t = useT();
  const session = useSession();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const docsBase = (import.meta.env.VITE_KOVIX_DOCS_URL as string | undefined)?.replace(/\/$/, '');
  const guideUrl = docsBase && navKey ? `${docsBase}/${navKey}` : null;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        className="kx-shell-icon-btn"
        title={t('shell.help')}
        aria-label={t('shell.help')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ShellIcon name="help" />
      </button>
      {open ? (
        <div className="kx-help-menu" role="menu" aria-label={t('shell.help')}>
          {guideUrl ? (
            <a
              className="kx-help-item"
              role="menuitem"
              href={guideUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
            >
              📘 {t('shell.helpGuide')}
              <small>{navKey}</small>
            </a>
          ) : (
            <div className="kx-help-item disabled" role="menuitem" aria-disabled tabIndex={-1}>
              📘 {t('shell.helpGuide')}
              <small>{t('shell.helpGuideMissing')}</small>
            </div>
          )}
          <div className="kx-help-item" role="menuitem" tabIndex={-1}>
            ⌨ {t('shell.helpShortcuts')}
            <small>
              <kbd>Ctrl</kbd>+<kbd>K</kbd> {t('shell.shortcutPalette')} · <kbd>/</kbd> {t('shell.shortcutSearch')} ·{' '}
              <kbd>Esc</kbd> {t('shell.shortcutEscape')}
            </small>
          </div>
          <Link className="kx-help-item" role="menuitem" to="/role-hub" search={{}} onClick={() => setOpen(false)}>
            🗺 {t('shell.helpRoleHub')}
          </Link>
          {session.hasModule('notify') ? (
            <Link
              className="kx-help-item"
              role="menuitem"
              to="/notifications/inbox"
              search={{}}
              onClick={() => setOpen(false)}
            >
              🔔 {t('shell.helpInbox')}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
